import type { CalendarResponse, MicrosoftCalendarAuthorization } from '@ai-meeting/shared'
import { apiClient } from './api'
import { getAccessToken } from './session'

const FLOW_KEY = 'brse.microsoft-calendar.pkce'
let callbackPromise: Promise<boolean> | null = null
let calendarRequest: {
  ownerToken: string | null
  limit: number | undefined
  promise: Promise<CalendarResponse>
} | null = null

/** Encode PKCE bytes without introducing a crypto library into the frontend bundle. */
function toBase64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Prepare calendar consent; keep its verifier in this tab, never in the authorization URL.
 * @returns Microsoft authorization URL; the caller performs a top-level navigation.
 */
export async function getMicrosoftCalendarConnectUrl(): Promise<string> {
  const verifier = toBase64Url(crypto.getRandomValues(new Uint8Array(32)))
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  const codeChallenge = toBase64Url(new Uint8Array(digest))
  const { data } = await apiClient.post<MicrosoftCalendarAuthorization>(
    '/api/calendar/microsoft/connect',
    { codeChallenge }
  )
  sessionStorage.setItem(
    FLOW_KEY,
    JSON.stringify({ verifier, state: data.state, createdAt: Date.now() })
  )
  return data.authorizationUrl
}

/**
 * Complete consent with the current BrSE JWT and tab-bound PKCE verifier.
 * Scrub the URL first; concurrent mounts share the request (React StrictMode).
 * @returns Whether a calendar callback was present.
 * @throws When consent was denied, tab state expired, or the backend rejects the exchange.
 */
export function completeMicrosoftCalendarCallback(): Promise<boolean> {
  if (callbackPromise) return callbackPromise
  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')
  const error = url.searchParams.get('error')
  if (!code && !error) return Promise.resolve(false)
  for (const key of ['code', 'state', 'error', 'error_description', 'session_state'])
    url.searchParams.delete(key)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  callbackPromise = (async () => {
    const stored = sessionStorage.getItem(FLOW_KEY)
    sessionStorage.removeItem(FLOW_KEY)
    if (error || !code || !state || !stored) throw new Error('Invalid calendar consent callback')
    const flow: { verifier?: string; state?: string; createdAt?: number } = JSON.parse(stored)
    if (
      flow.state !== state ||
      typeof flow.verifier !== 'string' ||
      !/^[A-Za-z0-9_-]{43}$/.test(flow.verifier) ||
      typeof flow.createdAt !== 'number' ||
      Date.now() - flow.createdAt > 10 * 60_000 ||
      flow.createdAt > Date.now()
    )
      throw new Error('Invalid calendar consent state')
    await apiClient.post('/api/calendar/microsoft/connection', {
      code,
      state,
      codeVerifier: flow.verifier,
    })
    return true
  })().finally(() => {
    callbackPromise = null
  })
  return callbackPromise
}

/**
 * Read the current user's Teams calendar without creating meetings or bot sessions.
 *
 * @param limit Ask the server for at most this many of the earliest events.
 *   The Home panel shows a handful and does not need the whole week.
 */
export function fetchMicrosoftCalendar(
  limit?: number
): Promise<CalendarResponse> {
  const ownerToken = getAccessToken()
  // The limit is part of the identity of an in-flight request: Home asks for a
  // few and the Upcoming page asks for all of them, and handing one the other's
  // answer would silently truncate a full listing.
  if (
    !calendarRequest ||
    calendarRequest.ownerToken !== ownerToken ||
    calendarRequest.limit !== limit
  ) {
    const promise = apiClient
      .get<CalendarResponse>(
        limit
          ? `/api/calendar/microsoft/events?limit=${limit}`
          : '/api/calendar/microsoft/events'
      )
      .then(({ data }) => data)
      .finally(() => {
        if (calendarRequest?.promise === promise) calendarRequest = null
      })
    calendarRequest = { ownerToken, limit, promise }
  }
  return calendarRequest.promise
}

/** Remove this account's calendar grant, leaving Microsoft SSO login untouched. */
export async function disconnectMicrosoftCalendar(): Promise<void> {
  await apiClient.delete('/api/calendar/microsoft/connection')
}
